/**
 * Utilitarios para manipulacao de cores
 * Gera shades (50-900) a partir de uma unica cor HEX
 */

interface HSL {
  h: number;
  s: number;
  l: number;
}

function hexToHSL(hex: string): HSL {
  hex = hex.replace(/^#/, "");

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };

  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Gera 10 shades (50-900) a partir de uma cor HEX base
 */
export function generateShades(hex: string): Record<string, string> {
  const { h, s } = hexToHSL(hex);

  // Lightness values for each shade
  const shadeMap: Record<string, number> = {
    "50": 95,
    "100": 90,
    "200": 80,
    "300": 70,
    "400": 55,
    "500": 45,  // base
    "600": 38,
    "700": 30,
    "800": 22,
    "900": 15,
  };

  // Adjust based on the original color's lightness
  const baseHSL = hexToHSL(hex);
  const baseLightness = baseHSL.l;

  const shades: Record<string, string> = {};
  for (const [shade, targetL] of Object.entries(shadeMap)) {
    // Scale lightness relative to the base color
    let adjustedL: number;
    if (shade === "500") {
      adjustedL = baseLightness;
    } else {
      const ratio = targetL / 45; // 45 is the default base lightness
      adjustedL = Math.max(0, Math.min(100, baseLightness * ratio));
    }
    shades[shade] = hslToHex(h, s, adjustedL);
  }

  return shades;
}

/**
 * Converte HEX para "R G B" string (formato esperado pelas CSS variables do Tailwind)
 */
function hexToRGBString(hex: string): string {
  hex = hex.replace(/^#/, "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `${r} ${g} ${b}`;
}

/**
 * Aplica shades como CSS variables no root
 * Formato: "R G B" (sem #) para compatibilidade com rgb(var(--x) / <alpha>)
 */
export function applyThemeColors(primary: string, accent?: string): void {
  const root = document.documentElement;
  const primaryShades = generateShades(primary);

  for (const [shade, color] of Object.entries(primaryShades)) {
    root.style.setProperty(`--primary-${shade}`, hexToRGBString(color));
  }

  if (accent) {
    root.style.setProperty("--accent-500", hexToRGBString(accent));
  }
}

/**
 * Remove CSS variables customizadas do root (volta ao default)
 */
export function removeThemeColors(): void {
  const root = document.documentElement;
  const shades = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900"];

  for (const shade of shades) {
    root.style.removeProperty(`--primary-${shade}`);
  }
  root.style.removeProperty("--accent-500");
}

/**
 * Valida se uma string e um HEX color valido
 */
export function isValidHex(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}
